import styled from '@emotion/styled'
import { FunctionComponent } from 'react'
import { Color, Colors, ColorUtil } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { BorderRadius, Transitions } from '~/assets/style/tokens'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { WithClassName } from '~/types/utils'
import { Icon, IconName } from './Icon'
import { Tag } from './Tag'

const CardSide = styled.div`
  display: flex;
  flex-direction: column;
  padding: 11px 12px 11px 11px;
  border-radius: ${BorderRadius.value} 0 0 ${BorderRadius.value};
  border: 1px solid transparent;
  border-right: none;
`
const CardIcon = styled(Icon)``

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 15px 11px 16px;
  border-radius: 0 ${BorderRadius.value} ${BorderRadius.value} 0;
  border: 1px solid transparent;
  border-left: none;
  flex-grow: 1;
  transition: all ${Transitions.quick};
`

const CardBodyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

/** Pushed to the far edge so the badge reads as a note about the card, not as part of its title. */
const CardBadge = styled(Tag)`
  margin-left: auto;
`
const CardTitle = styled.span`
  ${Fonts.P0}
`

const CardWrapper = styled.div<{ color: Color }>`
  display: flex;
  background: ${Colors.White};
  ${Fonts.P1}
  cursor: pointer;

  :hover {
    ${CardBody} {
      border: 1px solid ${({ color }) => ColorUtil.hexOpacity(color, 0.2)};
      border-left: none;
    }
  }

  :active {
    & ${CardSide} {
      background-color: ${({ color }) => ColorUtil.hexOpacity(color, 0.3)};
    }

    ${CardBody} {
      border: 1px solid ${({ color }) => ColorUtil.hexOpacity(color, 0.3)};
      border-left: none;
    }
  }

  & ${CardSide} {
    background-color: ${({ color }) => ColorUtil.hexOpacity(color, 0.2)};

    & ${CardIcon} {
      color: ${({ color }) => color};
    }
  }
`

export type AccessCardProps = WithClassName & {
  title: string
  content: string
  color: Color
  icon: IconName
  onClick: () => unknown
  trackingLabel: TrackingLabel
  /** A short note about the card itself rather than about what it links to — "Moved", and so far only that. */
  badge?: string
}
const BaseAccessCard: FunctionComponent<AccessCardProps> = ({ className, title, content, onClick, color, icon, trackingLabel, badge }) => {
  return (
    <CardWrapper color={color} className={className} onClick={onClick} data-tracking={trackingLabel}>
      <CardSide>
        <CardIcon width={'30px'} name={icon} />
      </CardSide>
      <CardBody>
        <CardBodyHeader>
          <CardTitle>{title}</CardTitle>
          <Icon width={'20px'} name={'chevron_right'} />
          {badge && <CardBadge color={Colors.SlateGrey}>{badge}</CardBadge>}
        </CardBodyHeader>
        <p>{content}</p>
      </CardBody>
    </CardWrapper>
  )
}

export const AccessCard = styled(BaseAccessCard)``
